import { AddressEntity, BusinessEntity } from 'Models/Entities';
import { Button, Display } from 'Views/Components/Button/Button';
import { observer } from 'mobx-react';

import BusinessEntityFields from './BusinessEntityFields';
import ButtonAsyncState from '../Button/ButtonAsyncState';
import Modal from 'Views/Components/Modal/Modal';
import React, { useState } from 'react';
import { runInAction } from 'mobx';
import { BusinessValidation } from '../BusinessNumberInputs/BusinessValidationModel';
import { useEntityChangeTracker } from 'Hooks/useHasChanged';
import { BusinessModifiableFields } from './BusinessEntityDetails';
import { AddressModifiableFields } from 'Models/Entities/AddressEntity';
import { PeekAtModel } from '../PeekAtModel/PeekAtModel';

export type AddBusinessDetailsModalProps = {
	businessEntity: BusinessEntity,
	organisationEmail?: string,
	isPrimaryBusinessEntity: boolean,
	additionalValidationFields: string[],
	onClose: () => void,
	onSubmit: () => Promise<void>,
}

const AddBusinessDetailsModal: React.FunctionComponent<AddBusinessDetailsModalProps> = observer(props => {
	const {
		businessEntity: originalBusinessEntity,
		organisationEmail,
		isPrimaryBusinessEntity,
		additionalValidationFields,
		onClose,
		onSubmit,
	} = props;

	const [abnacnDetail, setAbnAcnDetail] = useState<BusinessValidation>(new BusinessValidation());
	const [trusteeDetail, setTrusteeDetail] = useState<BusinessValidation>(new BusinessValidation());

	const [businessEntity] = useState(() => {
		const business = new BusinessEntity(originalBusinessEntity)
		// Assign default record so we have a model to modify properties on
		runInAction(() => business.address ??= new AddressEntity(originalBusinessEntity.address))
		return business;
	});

	const {
		isChanged: businessHasChanged,
	} = useEntityChangeTracker(BusinessEntity, businessEntity, BusinessModifiableFields);


	const {
		isChanged: addressHasChanged,
	} = useEntityChangeTracker(AddressEntity, businessEntity.address!, AddressModifiableFields);


	const onSavePressed = async () => {
		const options = {
			abnEntityType: abnacnDetail?.abnExtraDetails.typeCode ?? ""
		}
		BusinessModifiableFields.forEach(f => businessEntity.validateField(f, options));

		if (!canSave()) return;

		// Save all updated fields back to original business entity
		runInAction(() => originalBusinessEntity.assignAttributes(businessEntity));
		await onSubmit();
	};

	function canSave() {

		const validBusinessEntity = Object.keys(businessEntity.errors).length === 0;
		const validAbnAcnDetails = Object.keys(abnacnDetail.errors).length === 0;
		const validTrusteeDetails = Object.keys(trusteeDetail.errors).length === 0;

		return (businessHasChanged || addressHasChanged)
			&& validBusinessEntity
			&& validAbnAcnDetails
			&& validTrusteeDetails;
	}

	return (
		<Modal
			isOpen
			label="Add business entity"
			onRequestClose={onClose}
			className="access-modal"
		>
			<h4>Add business entity</h4>
			<BusinessEntityFields
				isPrimary={isPrimaryBusinessEntity}
				businessEntity={businessEntity}
				originalBusinessEntity={businessEntity}
				organisationEmail={organisationEmail}
				setAbnAcnDetail={setAbnAcnDetail}
				setTrusteeDetail={setTrusteeDetail}
				addNew
			/>
			<div key="actions" className="modal__actions">
				<Button key="cancel" onClick={onClose} display={Display.Outline}>Cancel</Button>
				<ButtonAsyncState
					key="confirm"
					onPress={onSavePressed}
					readonly={!canSave()}
					display={Display.Solid}
				>
					Add
				</ButtonAsyncState>
			</div>
		</Modal>

	);
});
export default AddBusinessDetailsModal;
